import { NavLink } from "react-router-dom";
import { useFarm } from "../context/FarmContext";
import { useAuth } from "../context/AuthContext";
import { CURRENCIES, LIVESTOCK, livestockById } from "../lib/constants";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import {
  LayoutDashboard, ListChecks, Wallet, Lightbulb,
  Sprout, Moon, Sun, Beef, Egg, Milk, PiggyBank, Cloud, LineChart, SlidersHorizontal, CalendarRange, LogOut,
} from "lucide-react";

const ICONS = { Beef, Egg, Milk, PiggyBank, Cloud, Sprout };

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/activities", label: "Activities", icon: ListChecks },
  { to: "/crops", label: "Crops", icon: Sprout },
  { to: "/yields", label: "Yields", icon: LineChart },
  { to: "/monthly", label: "Monthly", icon: CalendarRange },
  { to: "/expenses", label: "Expenses", icon: Wallet },
  { to: "/insights", label: "Insights", icon: Lightbulb },
  { to: "/settings", label: "Farm Profile", icon: SlidersHorizontal },
];

export default function Navbar() {
  const { settings, updateSettings, setActiveLivestock, theme, toggleTheme, alertCount } = useFarm();
  const { user, logout } = useAuth();
  if (!settings) return null;
  const active = livestockById(settings.active_livestock);
  const ActiveIcon = ICONS[active.icon] || Beef;

  const onCurrency = (code) => {
    const c = CURRENCIES.find((x) => x.code === code);
    updateSettings({ currency_code: c.code, currency_symbol: c.symbol });
  };

  return (
    <header className="no-print sticky top-0 z-50 glass-bar border-b border-border">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-white border border-border overflow-hidden">
              <img src="/logo.png" alt="AgriPulse logo" className="h-7 w-7 object-contain" data-testid="app-logo" />
            </div>
            <div className="leading-tight">
              <p className="font-display text-lg font-extrabold tracking-tight">AgriPulse</p>
              <p className="hidden sm:block text-[10px] uppercase tracking-widest text-muted-foreground font-mono-nums">
                Livestock Ledger
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Select value={settings.active_livestock} onValueChange={setActiveLivestock}>
              <SelectTrigger data-testid="livestock-type-select" className="w-[130px] sm:w-[150px] h-9 border-primary/25">
                <div className="flex items-center gap-2 truncate">
                  <ActiveIcon className="h-4 w-4 text-accent shrink-0" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                {LIVESTOCK.map((l) => {
                  const I = ICONS[l.icon] || Beef;
                  return (
                    <SelectItem key={l.id} value={l.id} data-testid={`livestock-option-${l.id}`}>
                      <span className="flex items-center gap-2"><I className="h-4 w-4" />{l.name}</span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Select value={settings.currency_code} onValueChange={onCurrency}>
              <SelectTrigger data-testid="currency-select" className="w-[92px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[320px]">
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                    {c.symbol} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              data-testid="theme-toggle-button"
              variant="outline" size="icon" onClick={toggleTheme}
              className="h-9 w-9 shrink-0"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {user && (
              <span className="hidden md:inline text-sm text-muted-foreground max-w-[140px] truncate" data-testid="nav-username" title={user.username}>
                {user.username}
              </span>
            )}
            <Button
              data-testid="logout-button"
              variant="outline" size="icon" onClick={logout}
              className="h-9 w-9 shrink-0" title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <nav className="flex items-center gap-1 overflow-x-auto pb-2 -mb-px scrollbar-none">
          {NAV.filter((item) => item.to !== "/crops" || active.id === "plants").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.to === "/activities" ? active.register : item.label}
              {item.to === "/monthly" && alertCount > 0 && (
                <span data-testid="monthly-alert-badge" className="ml-0.5 grid min-w-[18px] h-[18px] place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                  {alertCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
